import {
  log as logImpression,
  ModuleType,
  CTAType,
} from "@indeed/signals-schema/com/indeed/signals/schema/jspremium/Impression";
import React, { useEffect } from "react";

import { useLocale } from "../../../../../shared/I18n";
import { getTareData } from "../../../../../shared/native-modules/tare/TareDataContext";
import { usePromise } from "../../../../../shared/util/usePromise";
import { useViewjobOneGraphModel } from "../../../context/ViewJobDataContext";
import { ApplicantInsightsContent } from "./components/ApplicantInsightsContent";
import { BlurredApplicantInsightsPreviewCTA } from "./components/BlurredApplicantInsightsPreviewCTA";
import type { IApplicantInsightsProps } from "./types";

export const ApplicantInsights: React.FunctionComponent<IApplicantInsightsProps> = (
  props: IApplicantInsightsProps,
) => {
  const { hostId, country, accountId, parentLogId, pageId, cta, providerId } = props;
  const showCTA = !!cta;

  useEffect(() => {
    if (accountId === 0) {
      // Skip this condition explicitly.
      // accountId becomes 0 in the first rendering because accountId is derived by usePromise(getTareData)
      return;
    }
    logImpression({
      hostId: hostId,
      provider: providerId,
      country: {
        string: country,
      },
      ctaType: {
        CTAType: CTAType.PREMIUM,
      },
      accountId,
      parentLogId: {
        string: parentLogId,
      },
      pageId: {
        string: pageId,
      },
      // Dropping isPrivileged and proctorGroups
      // These should be available by joining mobsearch index
      moduleType: {
        ModuleType: showCTA ? ModuleType.CTA : ModuleType.APPLICANT_INSIGHTS,
      },
    });
  }, [pageId, hostId, country, accountId, parentLogId, showCTA, providerId]);

  return !showCTA ? (
    <ApplicantInsightsContent {...props} />
  ) : props.previewInsight ? (
    <BlurredApplicantInsightsPreviewCTA {...props} />
  ) : null;
};

type ApplicantInsightsWithContextProps = {
  reload: () => void;
};

export const ApplicantInsightsWithContext = (props: ApplicantInsightsWithContextProps) => {
  const { reload } = props;

  const vjModel = useViewjobOneGraphModel();
  const { country } = useLocale();
  const [, tareDataModel] = usePromise(getTareData);

  const isJsPremiumApplicantInsightsTogActive =
    vjModel?.features?.isJsPremiumApplicantInsightsTogActive;
  const applicantInsightsModel = vjModel?.applicantInsightsModel;
  const accountId = parseInt(tareDataModel?.accountStatus?.accountId ?? "0", 10);

  if (!isJsPremiumApplicantInsightsTogActive || !applicantInsightsModel) {
    return null;
  }
  return (
    <ApplicantInsights
      {...applicantInsightsModel}
      accountId={accountId}
      parentLogId={vjModel.mobvjtk}
      country={country ?? ""}
      pageId="viewjob"
      hostId="jobsearch"
      providerId="react-native-applicant-insights"
      reload={reload}
    />
  );
};
